import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Loader,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { config } from "@/shared/lib/config";
import { formatDateLocal, formatTime24 } from "@/shared/lib/date-time";
import { fetcher } from "@/shared/lib/fetcher";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import type {
  OrderFilters,
  OrderPagination,
  OrderSort,
} from "../../domain/repo/get-orders";
import type { Shift } from "../../domain/repo/get-shifts";
import { useLatestShift, useOpenShift, useOrders, usePaymentMethods, useCarryOverOpenOrders } from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import {
  listLocalOrdersForBranch,
  listLocalOrdersForShift,
  listOpenLocalOrdersForBranch,
  OFFLINE_SYNC_CHANGED_EVENT,
} from "../lib/offline-sync/db";
import { formatLocalCustomerDisplay } from "../lib/offline-sync/local-first-display-helpers";
import type { LocalOrderRecord } from "../lib/offline-sync/types";
import { OrderCard } from "../ui/OrderCard";
import type { OrdersListSearch } from "./orders-list-search";

const getLocalDerivedStatus = (order: LocalOrderRecord): string => {
  const refundTotal = order.refundTotal ?? 0;
  if (refundTotal >= order.totals.grandTotal && order.totals.grandTotal > 0) {
    return "REFUNDED";
  }
  if (refundTotal > 0) return "PARTIAL_REFUNDED";
  return order.status;
};

export function OrdersPage() {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const deviceId = selection?.deviceId ?? null;
  const navigate = useNavigate({ from: "/app/pos/orders" });
  const router = useRouter();
  const goBack = () => router.history.back();
  const search = useSearch({ from: "/app/pos/orders" }) as OrdersListSearch;

  const [qInput, setQInput] = useState(() => search.q ?? "");
  useEffect(() => {
    setQInput(search.q ?? "");
  }, [search.q]);

  // Default shift: open shift first, then latest closed shift
  const { data: openShift } = useOpenShift(branchId, deviceId);
  const { data: latestShift } = useLatestShift(branchId);
  const defaultShiftId = openShift?.id ?? latestShift?.id;
  const { data: paymentMethods = [], isLoading: isLoadingPaymentMethods } =
    usePaymentMethods(branchId);

  const patchSearch = useCallback(
    (patch: Partial<OrdersListSearch>) => {
      navigate({
        search: (prev) => ({ ...(prev as OrdersListSearch), ...patch }),
        replace: true,
      });
    },
    [navigate],
  );

  // Format shift display name
  const formatShiftLabel = (shift: Shift): string => {
    const dateStr = formatDateLocal(shift.openedAt);
    const timeStr = formatTime24(shift.openedAt);
    const statusStr = shift.status === "OPEN" ? " (ເປີດ)" : " (ປິດ)";
    return `${dateStr} ${timeStr}${statusStr}`;
  };

  // Lookup URL for shifts
  const shiftsLookupUrl = branchId
    ? `${config.apiUrl}/pos/shifts/lookup?branchId=${branchId}`
    : "";

  // Wrapper functions for InfiniteCombobox
  const fetchShifts = async ({
    search,
    pageParam,
  }: {
    search: string;
    pageParam: number;
  }): Promise<{
    items: Array<Shift & { name: string }>;
    nextPage: number | null;
  }> => {
    if (!branchId) {
      return { items: [], nextPage: null };
    }
    const limit = 20;
    const page = typeof pageParam === "number" && pageParam > 0 ? pageParam : 1;
    const skip = (page - 1) * limit;

    const url = new URL(shiftsLookupUrl);
    if (search && search.trim().length > 0) {
      url.searchParams.set("q", search);
    }
    url.searchParams.set("limit", String(limit));
    url.searchParams.set("skip", String(skip));

    const data = await fetcher.get<{
      items: Shift[];
      total?: number;
    }>(url.toString());
    const items = Array.isArray(data.items) ? data.items : [];
    const total = typeof data.total === "number" ? data.total : items.length;
    const nextPage = skip + limit < total ? page + 1 : null;

    // Convert shifts to lookup items with name
    return {
      items: items.map((shift) => ({
        ...shift,
        name: formatShiftLabel(shift),
      })),
      nextPage,
    };
  };

  const preloadShift = async (
    id: string,
  ): Promise<(Shift & { name: string }) | null> => {
    if (!branchId || !id) return null;
    const url = `${config.apiUrl}/pos/shifts/lookup/${encodeURIComponent(id)}?branchId=${branchId}`;
    const data = await fetcher.get<{ item?: Shift | null }>(url);
    const shift = data.item;
    if (!shift) return null;
    return { ...shift, name: formatShiftLabel(shift) };
  };

  const pageSize = 20;
  const page = search.page;

  const shiftIdForQuery = search.shiftId ?? defaultShiftId;
  const isViewingCurrentShift =
    !search.shiftId || search.shiftId === defaultShiftId;

  const filters: OrderFilters = {
    status: search.status ? [search.status] : undefined,
    orderType: search.orderType ? [search.orderType] : undefined,
    shiftId: shiftIdForQuery || undefined,
    search: search.q || undefined,
    paymentMethodId: search.paymentMethodId,
  };

  const pagination: OrderPagination = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
  };

  const sort: OrderSort = {
    field: search.sortField,
    order: search.sortOrder,
  };

  const { data, isLoading } = useOrders(branchId, filters, pagination, sort);

  const showCarryOver =
    isViewingCurrentShift &&
    !!shiftIdForQuery &&
    page === 1 &&
    (!filters.status?.length || filters.status.includes("OPEN"));

  const carryOverFilters = {
    orderType: filters.orderType,
    search: filters.search,
  };

  const { data: carryOverData } = useCarryOverOpenOrders(
    branchId,
    shiftIdForQuery ?? null,
    carryOverFilters,
    { enabled: showCarryOver },
  );

  const [localOrders, setLocalOrders] = useState<LocalOrderRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadLocalOrders = async () => {
      // Recovery fallback: when the active shift cannot be resolved (e.g.
      // offline right after a reboot) load local orders by branch so previously
      // saved offline orders are never hidden.
      let records = shiftIdForQuery
        ? await listLocalOrdersForShift(shiftIdForQuery)
        : branchId
          ? await listLocalOrdersForBranch(branchId)
          : [];

      if (showCarryOver && branchId && shiftIdForQuery) {
        const openBranchOrders = await listOpenLocalOrdersForBranch(branchId);
        const carryOverLocal = openBranchOrders.filter(
          (record) => record.shiftId !== shiftIdForQuery,
        );
        const existingIds = new Set(records.map((record) => record.clientOrderId));
        records = [
          ...carryOverLocal.filter(
            (record) => !existingIds.has(record.clientOrderId),
          ),
          ...records,
        ];
      }

      if (shiftIdForQuery && branchId) {
        const branchOrders = await listLocalOrdersForBranch(branchId);
        const paidInCurrentShift = branchOrders.filter(
          (record) =>
            record.completedShiftId === shiftIdForQuery &&
            record.shiftId !== shiftIdForQuery,
        );
        const existingIds = new Set(
          records.map((record) => record.clientOrderId),
        );
        records = [
          ...paidInCurrentShift.filter(
            (record) => !existingIds.has(record.clientOrderId),
          ),
          ...records,
        ];
      }

      if (!cancelled) {
        setLocalOrders(
          records.filter((record) =>
            ["OPEN", "PAID", "VOID", "MERGED", "NEEDS_REVIEW"].includes(
              record.status,
            ),
          ),
        );
      }
    };

    void loadLocalOrders();
    window.addEventListener(OFFLINE_SYNC_CHANGED_EVENT, loadLocalOrders);
    return () => {
      cancelled = true;
      window.removeEventListener(OFFLINE_SYNC_CHANGED_EVENT, loadLocalOrders);
    };
  }, [shiftIdForQuery, branchId, showCarryOver]);

  const localOrdersForView = localOrders
    .filter((order) => order.branchId === branchId)
    .filter((order) => {
      const derivedStatus = getLocalDerivedStatus(order);
      if (filters.status?.length && !filters.status.includes(derivedStatus)) {
        return false;
      }
      const orderType = order.isDelivery
        ? "DELIVERY"
        : order.selection.tableId
          ? "DINE_IN"
          : "TAKEAWAY";
      if (filters.orderType?.length && !filters.orderType.includes(orderType)) {
        return false;
      }
      if (
        filters.paymentMethodId &&
        !order.payments.some(
          (payment) => payment.methodId === filters.paymentMethodId,
        )
      ) {
        return false;
      }
      const searchText = filters.search?.trim().toLowerCase();
      if (!searchText) return true;
      return (
        String(order.clientOrderNo).includes(searchText) ||
        order.clientOrderId.toLowerCase().includes(searchText) ||
        (order.selection.tableName ?? "").toLowerCase().includes(searchText) ||
        (order.customerSnapshot?.phone ?? "").includes(searchText) ||
        `${order.customerSnapshot?.firstName ?? ""} ${order.customerSnapshot?.lastName ?? ""}`
          .toLowerCase()
          .includes(searchText)
      );
    })
    .map((order) => {
      const orderType = order.isDelivery
        ? "DELIVERY"
        : order.selection.tableId
          ? "DINE_IN"
          : "TAKEAWAY";
      return {
        id: order.clientOrderId,
        orderNo: `L${order.clientOrderNo}`,
        branchId: order.branchId,
        deviceId: order.deviceId,
        orderType,
        status: getLocalDerivedStatus(order),
        tableId: order.selection.tableId ?? null,
        customerId: order.customerId ?? null,
        createdBy: order.selection.salespersonId ?? null,
        shiftId: order.shiftId,
        seatLabel: null,
        guestCount: null,
        note: null,
        subtotal: order.totals.subtotal,
        discountTotal: order.totals.discountTotal,
        taxTotal: order.totals.taxTotal,
        serviceChargeTotal: order.totals.serviceChargeTotal,
        grandTotal: order.totals.grandTotal,
        paidTotal: 0,
        openedAt: order.createdAt,
        closedAt: null,
        finalizedAt: null,
        metadata: { local: true },
        createdAt: order.createdAt,
        updatedAt: order.updatedAt,
        tableName: order.selection.tableName ?? null,
        salespersonName: order.selection.salespersonName ?? null,
        customerDisplay: formatLocalCustomerDisplay(order.customerSnapshot),
        isLocal: true,
        syncStatus: order.syncStatus,
        lastSyncError: order.lastSyncError,
        isCarryOverFromPriorShift:
          !!shiftIdForQuery &&
          order.shiftId !== shiftIdForQuery &&
          getLocalDerivedStatus(order) === "OPEN",
        isPaidInCurrentShift:
          !!shiftIdForQuery &&
          order.completedShiftId === shiftIdForQuery &&
          order.shiftId !== shiftIdForQuery,
      };
    })
    .sort((a, b) => {
      if (sort.field === "grandTotal") {
        const diff = a.grandTotal - b.grandTotal;
        return sort.order === "asc" ? diff : -diff;
      }
      const diff = a.createdAt.localeCompare(b.createdAt);
      return sort.order === "asc" ? diff : -diff;
    });

  const carryOverServerForView =
    showCarryOver && !filters.paymentMethodId
      ? (carryOverData?.data ?? []).map((order) => ({
          ...order,
          customerDisplay: null as string | null,
          isLocal: false as const,
          isCarryOverFromPriorShift: true as const,
        }))
      : [];

  const dedupeOrdersById = <
    T extends { id: string },
  >(
    items: T[],
  ): T[] => {
    const seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };

  const ordersForView =
    page === 1
      ? dedupeOrdersById([
          ...localOrdersForView,
          ...carryOverServerForView,
          ...(data?.data ?? []).map((order) => ({
            ...order,
            customerDisplay: null as string | null,
            isLocal: false as const,
            isCarryOverFromPriorShift: false as const,
            isPaidInCurrentShift:
              !!shiftIdForQuery &&
              order.completedShiftId === shiftIdForQuery &&
              order.shiftId !== shiftIdForQuery,
          })),
        ])
      : (data?.data ?? []).map((order) => ({
          ...order,
          customerDisplay: null as string | null,
          isLocal: false as const,
          isCarryOverFromPriorShift: false as const,
          isPaidInCurrentShift:
            !!shiftIdForQuery &&
            order.completedShiftId === shiftIdForQuery &&
            order.shiftId !== shiftIdForQuery,
        }));

  const totalOrders =
    (data?.total ?? 0) +
    localOrdersForView.length +
    (page === 1 ? carryOverServerForView.length : 0);

  const totalPages = Math.ceil(totalOrders / pageSize);

  const handleStatusChange = (value: string) => {
    if (value === "all") {
      patchSearch({ status: undefined, page: 1 });
    } else {
      patchSearch({ status: value, page: 1 });
    }
  };

  const handleOrderTypeChange = (value: string) => {
    if (value === "all") {
      patchSearch({ orderType: undefined, page: 1 });
    } else {
      patchSearch({ orderType: value, page: 1 });
    }
  };

  const handleShiftChange = (value: string) => {
    const id = value.trim();
    if (!id) {
      patchSearch({ shiftId: undefined, page: 1 });
      return;
    }
    const sameAsDefault = defaultShiftId && id === defaultShiftId;
    patchSearch({
      shiftId: sameAsDefault ? undefined : id,
      page: 1,
    });
  };

  const handlePaymentMethodChange = (value: string) => {
    if (value === "all") {
      patchSearch({ paymentMethodId: undefined, page: 1 });
    } else {
      patchSearch({ paymentMethodId: value, page: 1 });
    }
  };

  const debouncedSearchChange = useDebounceCallback((value: string) => {
    const trimmed = value.trim();
    patchSearch({
      q: trimmed.length > 0 ? trimmed : undefined,
      page: 1,
    });
  }, 500);

  const handleSortFieldChange = (value: string) => {
    patchSearch({
      sortField: value === "grandTotal" ? "grandTotal" : "createdAt",
      page: 1,
    });
  };

  const handleSortOrderChange = (value: string) => {
    patchSearch({
      sortOrder: value === "asc" ? "asc" : "desc",
      page: 1,
    });
  };

  const handleClearFilters = () => {
    patchSearch({
      status: undefined,
      orderType: undefined,
      shiftId: undefined,
      q: undefined,
      paymentMethodId: undefined,
      page: 1,
    });
  };

  const comboboxShiftValue = search.shiftId ?? defaultShiftId ?? "";

  const hasActiveFilters =
    !!search.status ||
    !!search.orderType ||
    (!!search.shiftId && search.shiftId !== defaultShiftId) ||
    !!search.q ||
    !!search.paymentMethodId;

  if (!branchId) {
    return (
      <div className="container mx-auto max-w-7xl p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Search className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ມີສາຂາ</EmptyTitle>
            <EmptyDescription>ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນ</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-h-[calc(100svh-10rem)] max-w-7xl overflow-y-auto p-4">
      <div className="mb-6">
        <div className="mb-4 flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-bold text-2xl">ລາຍການ Order</h1>
        </div>

        {/* Filters Section */}
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-medium text-sm">ຕົວກອງການຄົ້ນຫາ</h2>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="h-8"
              >
                <X className="mr-2 h-4 w-4" />
                ລຶບຕົວກອງ
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Status Filter */}
            <Select
              value={search.status ?? "all"}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="ສະຖານະ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ສະຖານະທັງໝົດ</SelectItem>
                <SelectItem value="OPEN">ເປີດ</SelectItem>
                <SelectItem value="PAID">ຊຳລະແລ້ວ</SelectItem>
                <SelectItem value="VOID">ຍົກເລີກ</SelectItem>
                <SelectItem value="REFUNDED">ຄືນເງິນ</SelectItem>
                <SelectItem value="PARTIAL_REFUNDED">ຄືນເງິນບາງສ່ວນ</SelectItem>
                <SelectItem value="MERGED">ລວມບິນ</SelectItem>
              </SelectContent>
            </Select>

            {/* Order Type Filter */}
            <Select
              value={search.orderType ?? "all"}
              onValueChange={handleOrderTypeChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="ປະເພດ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ປະເພດທັງໝົດ</SelectItem>
                <SelectItem value="DINE_IN">ກິນໃນຮ້ານ</SelectItem>
                <SelectItem value="TAKEAWAY">ສັ່ງກັບບ້ານ</SelectItem>
                <SelectItem value="DELIVERY">ຈັດສົ່ງ</SelectItem>
              </SelectContent>
            </Select>

            {/* Shift Filter */}
            {branchId ? (
              <InfiniteCombobox<Shift & { name: string }>
                queryKey={["pos", "shifts", "lookup", branchId]}
                queryFn={fetchShifts}
                preloadQueryFn={preloadShift}
                getLabel={(shift) => shift.name}
                getValue={(shift) => shift.id}
                value={comboboxShiftValue}
                onValueChange={handleShiftChange}
                placeholder="ເລືອກ Shift"
                clearable
              />
            ) : (
              <div className="flex h-10 items-center">
                <Loader />
              </div>
            )}

            {/* Payment method filter — ອໍເດີທີ່ມີການຊຳລະດ້ວຍວິທີນີ້ */}
            <Select
              value={search.paymentMethodId ?? "all"}
              onValueChange={handlePaymentMethodChange}
              disabled={isLoadingPaymentMethods}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="ວິທີຊຳລະ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ວິທີຊຳລະທັງໝົດ</SelectItem>
                {paymentMethods.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Search and Sort */}
        <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              value={qInput}
              onChange={(e) => {
                const v = e.target.value;
                setQInput(v);
                debouncedSearchChange(v);
              }}
              className="pl-10"
              placeholder="ຄົ້ນຫາໂດຍເລກ Order..."
            />
          </div>

          <div className="flex gap-2">
            <Select
              value={search.sortField}
              onValueChange={handleSortFieldChange}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">ວັນທີ</SelectItem>
                <SelectItem value="grandTotal">ລາຄາ</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={search.sortOrder}
              onValueChange={handleSortOrderChange}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">ລົງ</SelectItem>
                <SelectItem value="asc">ຂຶ້ນ</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      ) : ordersForView.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Search className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ພົບ Order</EmptyTitle>
            <EmptyDescription>ບໍ່ມີ Order ທີ່ກົງກັບການຄົ້ນຫາ</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {ordersForView.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() =>
                        patchSearch({ page: Math.max(1, page - 1) })
                      }
                      className={
                        page === 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              onClick={() => patchSearch({ page: pageNum })}
                              isActive={page === pageNum}
                              className="cursor-pointer"
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    },
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        patchSearch({
                          page: Math.min(totalPages, page + 1),
                        })
                      }
                      className={
                        page === totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
}
